import { initializeApp } from 'firebase/app';
import { 
    getFirestore, 
    collection, 
    doc, 
    getDoc, 
    onSnapshot,
    updateDoc,
    Timestamp as FirebaseTimestamp,
    getDocs,
    deleteField,
    setDoc,
    addDoc
} from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL, uploadBytesResumable } from 'firebase/storage';
import { Campaign, convertToCampaign } from '../models/Campaign';

// Re-export Timestamp for use in other parts of the app
export const Timestamp = FirebaseTimestamp;

// Firebase configuration
const firebaseConfig = {
    apiKey: process.env.REACT_APP_API_KEY,
    authDomain: process.env.REACT_APP_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_PROJECT_ID,
    storageBucket: process.env.REACT_APP_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_APP_ID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
export const db = getFirestore(app);
const storage = getStorage(app);

// Campaign service
export class CampaignService {
    // Get all campaigns
    static getCampaigns = (callback: (campaigns: Campaign[]) => void) => {
        const campaignsCollection = collection(db, 'campaigns');
        
        return onSnapshot(campaignsCollection, (snapshot) => {
            const campaignList: Campaign[] = [];
            snapshot.forEach((doc) => {
                const data = doc.data();
                campaignList.push(convertToCampaign(doc.id, data));
            });
            callback(campaignList);
        });
    };

    // Get a single campaign by ID
    static getCampaignById = async (id: string): Promise<Campaign | null> => {
        const campaignDoc = doc(db, 'campaigns', id);
        const docSnap = await getDoc(campaignDoc);

        if (docSnap.exists()) {
            return convertToCampaign(docSnap.id, docSnap.data());
        }
        return null;
    };

    // Upload image to Firebase Storage
    static uploadImage = async (file: File, campaignId: string): Promise<string> => {
        const storageRef = ref(storage, `campaigns/${campaignId}/image.jpg`);
        await uploadBytes(storageRef, file);
        return getDownloadURL(storageRef);
    };

    // Upload video to Firebase Storage
    static uploadVideo = async (
        file: File, 
        campaignId: string, 
        progressCallback: (progress: number) => void
    ): Promise<string> => {
        const storageRef = ref(storage, `campaigns/${campaignId}/video.mp4`);
        
        return new Promise((resolve, reject) => {
            try {
                const uploadTask = uploadBytesResumable(storageRef, file);

                uploadTask.on('state_changed',
                    (snapshot) => {
                        const progress = snapshot.bytesTransferred / snapshot.totalBytes;
                        progressCallback(progress);
                    },
                    (error) => {
                        console.error('Upload error:', error);
                        reject(error);
                    },
                    async () => {
                        try {
                            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                            resolve(downloadURL);
                        } catch (error) {
                            console.error('Error getting download URL:', error);
                            reject(error);
                        }
                    }
                );
            } catch (error) {
                console.error('Error starting upload:', error);
                reject(error);
            }
        });
    };

    // Update a campaign
    static updateCampaign = async (id: string, campaign: Campaign): Promise<void> => {
        const campaignRef = doc(db, 'campaigns', id);
        const { id: _, ...campaignData } = campaign; // Remove id from the data
        await updateDoc(campaignRef, campaignData);
    };

    // Update campaign coordinates
    static updateCampaignCoordinates = async (
        id: string,
        latitude: number,
        longitude: number
    ): Promise<void> => {
        const campaignRef = doc(db, 'campaigns', id);
        await updateDoc(campaignRef, {
            latitude,
            longitude
        });
    };

    // Create a new campaign
    static createCampaign = async (campaign: Omit<Campaign, 'id'>): Promise<string> => {
        const campaignsCollection = collection(db, 'campaigns');
        
        // Make sure ad_variations exists, even if empty
        const campaignData = {
            ...campaign,
            ad_variations: campaign.ad_variations || []
        };
        
        const docRef = await addDoc(campaignsCollection, campaignData);
        return docRef.id;
    };
} 